import React from 'react'
import { SwiperSlide } from 'swiper/react'
import { array, string } from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { EMPTY_ARRAY, PROFESSIONAL_PLACEHOLDER_IMAGE, PROFESSIONALS, ALL } from '../../../utils/constants'
import { getInsightsObject, urlWithTrack, getImageOrDefaultSrc, getNameAbbr } from '../../../helpers/common'
import AdSwiper from '../../ui/ad-swiper/AdSwiper'
import AdLink from '../../ui/ad-link/AdLink'
import SpecListItem from '../../ui/specs/SpecListItem'
import Insights from '../Insights'
import { professionalsMessages } from '../messages'

/**
 * Component to render a widget with images like an  architects offices or companies widget
 *
 * @component
 * @example
 * return (
 *    <WidgetGridImages
 *      data={[
 *        {
 *          count: 6,
 *          countries: [{name: "United Kingdom", slug: "united-kingdom"}],
 *          images: [
 *            "https://images.adsttc.com/media/images/5ca6/0b1a/284d/d13b/1300/00c5/medium_jpg/HBA_ORNC_JamesBrittain_043.jpg",
 *            "https://images.adsttc.com/media/images/5c93/2285/284d/d177/ea00/00cd/medium_jpg/_E2A8865.jpg",
 *            "https://images.adsttc.com/media/images/5c93/2624/284d/d106/1900/01cd/medium_jpg/_E2A9031.jpg"
 *          ],
 *          logo: 'https://hayden.adsttc.com/m10rh16s7rsiu47ueaczx39hdkx5',
 *          name: "CannonDesign",
 *          speciality: "Lighting Design Consultancy",
 *          url: "https://www.archdaily.com/professional/cannondesign"
 *        }
 *      ]}
 *    />
 *  )
*/

const WidgetGridImages = ({
  data = EMPTY_ARRAY,
  widgetName = 'offices',
  searchType,
  query,
  scope }) => {
  const projectsCount = count => {
    if (count) {
      const projectsMessage = count === 1 ? { ...professionalsMessages.project } : { ...professionalsMessages.projects }
      const renderProjectsCount = (
        <span>
          {count}
          {' '}
          <FormattedMessage {...projectsMessage} />
        </span>
      )
      return renderProjectsCount
    }
  }

  // Reducing data length to 3 if is a Professionals Widget && Global Search (ALL)
  if (widgetName === PROFESSIONALS && searchType === ALL) data = data.slice(0, 3)

  return (
    <AdSwiper isLazy goToSlideNumber={3} customClasses='swiper-widget' autoView>
      {data.map((item, index) => {
        const insights = getInsightsObject(widgetName, item.name, index, true, searchType, query)
        const trackedUrl = urlWithTrack(item.url, searchType)
        return (
          <SwiperSlide lazy='true' key={item + index}>
            <Insights {...insights}>
              <a className='professionals--all' href={trackedUrl} key={item + index} title={item.name}>
                {item?.images?.map((img, index) =>
                  <div key={'image' + index} className='list-view__single-image-wrapper'>
                    <div
                      style={{ backgroundImage: `url("${getImageOrDefaultSrc(img, PROFESSIONAL_PLACEHOLDER_IMAGE)}")` }}
                      className='list-view__background-image'
                      data-object-fit='cover'
                    />
                  </div>
              )}
              </a>
              <div className='list-view__description'>
                {
                  item.logo
                    ? <img className='list-view__logo' src={item.logo} alt='Professional Logo' />
                    : <div className='list-view__logo list-view__logo--placeholder'>{getNameAbbr(item.name)}</div>
                }
                <div className='list-view__data'>
                  <AdLink className='list-view__professional-link' href={trackedUrl} >
                    <p className='list-view__name'>{item.name}</p>
                    <p className='list-view__speciality'>{item.speciality}</p>
                  </AdLink>
                  <div className='list-view__projects-info'>
                    <SpecListItem scope={scope} item='country' items={item.countries} isStringItem landingUrl={trackedUrl} />
                    {!!item.count && !!item.countries.length && <span> | </span>}
                    {projectsCount(item.count)}
                  </div>
                </div>
              </div>
            </Insights>
          </SwiperSlide>
        )
      }
      )}
    </AdSwiper>
  )
}

WidgetGridImages.propTypes = {
  /**
   * Widget data
  */
  data: array,
  /**
   * Type of search
  */
  searchType: string,
  /**
   * Search query value
  */
  query: string,
  /**
   * Widget name
  */
  widgetName: string,
  /**
   * Site
  */
  scope: string
}

export default WidgetGridImages
